import React from "react";
import { Link } from "react-router-dom";
import {Shield, Zap, DollarSign, Clock, CheckCircle, ArrowRight} from 'lucide-react';
import Testimonials from "../components/Testimonials";

const HomePage = () => {
  const features = [
    {
      icon: Shield,
      title: "Comprehensive Protection",
      description: "Coverage for the strip, the trailer, and the garage",
    },
    {
      icon: Zap,
      title: "Fast Quotes",
      description: "Get covered in minutes, not days",
    },
    {
      icon: DollarSign,
      title: "Affordable Rates",
      description: "Often cheaper than standard auto policies",
    },
    {
      icon: Clock,
      title: "24/7 Support",
      description: "We're here when you need us most",
    },
  ];

  const coverageTypes = [
    {
      title: "On-Track Coverage",
      description:
        "Protection during sanctioned racing events and time trials",
      image: "https://static.lumi.new/0e/0e22873f7e56cbb7c097fe77cd965eef.png",
    },
    {
      title: "Transport Protection",
      description: "Coverage for your vehicle and trailer during transport",
      image: "https://static.lumi.new/38/38c11b411bc5c8ae57dbb07cf0364cc8.png",
    },
    {
      title: "Storage Coverage",
      description:
        "Off-track storage protection for your high-value vehicle",
      image: "https://static.lumi.new/38/38c11b411bc5c8ae57dbb07cf0364cc8.png",
    },
  ];

  return (
    <div className="bg-black">
      {/* Hero Section */}
      <section className="relative h-[600px] md:h-[700px] flex items-center">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://static.lumi.new/a9/a9d6ee4473ea1779d26a030091b94994.png')",
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-transparent"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 z-10">
          <div className="max-w-2xl">
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
              Affordable{" "}
              <span className="text-[#DC0000]">Drag Car Insurance</span> &
              Protection
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-8">
              Specialized coverage for the strip, the trailer, and the garage.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/contact"
                className="bg-[#DC0000] text-white px-8 py-4 rounded-md font-semibold text-lg hover:bg-red-700 transition-all transform hover:scale-105 text-center"
              >
                Get a Quote Now
              </Link>
              <Link
                to="/coverage"
                className="bg-white text-black px-8 py-4 rounded-md font-semibold text-lg hover:bg-gray-200 transition-all text-center"
              >
                View Coverage
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="bg-[#1a1a1a] py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-3xl md:text-4xl font-bold text-[#DC0000] mb-2">
                10K+
              </div>
              <div className="text-gray-400 text-sm">Racers Insured</div>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-bold text-[#DC0000] mb-2">
                $500M+
              </div>
              <div className="text-gray-400 text-sm">Coverage Provided</div>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-bold text-[#DC0000] mb-2">
                24/7
              </div>
              <div className="text-gray-400 text-sm">Support Available</div>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-bold text-[#DC0000] mb-2">
                A+ Rated
              </div>
              <div className="text-gray-400 text-sm">Insurance Provider</div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold mb-4">
              Why Choose <span className="text-[#DC0000]">Drag Car Insurance</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              We understand the unique needs of drag racing enthusiasts. Get
              specialized drag racers insurance tailored to your high-performance
              vehicle.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-[#1a1a1a] p-6 rounded-lg border border-gray-800 hover:border-[#DC0000] transition-all transform hover:scale-105"
              >
                <feature.icon className="w-12 h-12 text-[#DC0000] mb-4" />
                <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                <p className="text-gray-400">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Coverage Types */}
      <section className="py-20 bg-[#1a1a1a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold mb-4">
              Comprehensive{" "}
              <span className="text-[#DC0000]">Drag Racers Insurance</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              From the track to storage, we've got you covered with specialized
              insurance for drag racing cars.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {coverageTypes.map((coverage, index) => (
              <div
                key={index}
                className="bg-black rounded-lg overflow-hidden border border-gray-800 hover:border-[#DC0000] transition-all transform hover:scale-105"
              >
                <div className="h-48 overflow-hidden">
                  <img
                    src={coverage.image}
                    alt={coverage.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-2xl font-semibold mb-3">
                    {coverage.title}
                  </h3>
                  <p className="text-gray-400 mb-4">{coverage.description}</p>
                  <Link
                    to="/coverage"
                    className="text-[#DC0000] font-semibold flex items-center hover:underline"
                  >
                    Learn More <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-5xl font-bold mb-6">
                Specialized <span className="text-[#DC0000]">Insurance</span>{" "}
                for High-Value Modified Vehicles
              </h2>
              <p className="text-xl text-gray-400 mb-8">
                Standard auto insurance doesn't cover drag racing. That's why we
                offer specialized drag race car insurance designed specifically
                for racers like you.
              </p>
              <ul className="space-y-4">
                {[
                  "Coverage during sanctioned events",
                  "Protection for modified and high-value vehicles",
                  "Liability and physical damage options",
                  "Off-track storage coverage",
                  "Transport and trailer protection",
                  "Flexible policy options",
                ].map((benefit, index) => (
                  <li key={index} className="flex items-start">
                    <CheckCircle className="w-6 h-6 text-[#DC0000] mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-300">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <img
                src="https://static.lumi.new/0e/0e22873f7e56cbb7c097fe77cd965eef.png"
                alt="Drag racing insurance"
                className="rounded-lg shadow-2xl"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent rounded-lg"></div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#DC0000]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-5xl font-bold mb-6 text-white">
            Ready to Get Protected?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Get your drag car insurance quote in minutes. Affordable rates for
            serious racers.
          </p>
          <Link
            to="/contact"
            className="inline-block bg-black text-white px-8 py-4 rounded-md font-semibold text-lg hover:bg-gray-900 transition-all transform hover:scale-105"
          >
            Get Your Free Quote
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
